/**
 * Global render-quality controller. Exposes a resolution `scale` in [min, 1]
 * that the App multiplies into the drawing-buffer pixel ratio. Adaptively
 * lowers the scale when fps drops below target and recovers when there is
 * headroom — in coarse steps so we don't reallocate buffers every frame.
 */
export class Quality {
  /** Current resolution scale, 0..1. Read by the App on resize. */
  scale = 1;

  private readonly min: number;
  private readonly targetFps: number;
  private cooldown = 0;

  constructor(opts: { min?: number; targetFps?: number } = {}) {
    this.min = opts.min ?? 0.5;
    this.targetFps = opts.targetFps ?? 55;
  }

  /**
   * Feed the smoothed fps each frame. Returns true when `scale` changed by a
   * step, so the caller knows to resize the drawing buffer.
   */
  update(fps: number, dt: number): boolean {
    if (this.cooldown > 0) {
      this.cooldown -= dt;
      return false;
    }
    const step = 0.1;
    let next = this.scale;
    // wide dead zone + long cooldown so the resolution doesn't oscillate
    // (which itself reads as flicker on heavy raymarch sketches)
    if (fps < this.targetFps - 14) {
      next = Math.max(this.min, this.scale - step);
    } else if (fps > this.targetFps + 18) {
      next = Math.min(1, this.scale + step);
    }
    if (Math.abs(next - this.scale) > 1e-3) {
      this.scale = next;
      this.cooldown = 3; // seconds before next adjustment
      return true;
    }
    return false;
  }
}
